import axios from '../axiosConfig';

export interface RoleApiItem {
  name: string;
  description: string;
  permissions: string[];
  userCount: number;
  id: string;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface RolesApiResponse {
  results: RoleApiItem[];
  totalCount: number;
  totalResults: number;
}

export interface RolesQueryParams {
  name?: string;
  description?: string;
  permissions?: string;
  textSearch?: string;
  page?: number;
  pageSize?: number;
  skip?: number;
  includeDeleted?: boolean;
  orderByField?: string;
  descending?: boolean;
}

export async function fetchRoles(params?: RolesQueryParams): Promise<RolesApiResponse> {
  const p = params || {};
  // API expects PascalCase query param names
  const query: any = {
    Name: p.name ?? '',
    Description: p.description ?? '',
    Permissions: p.permissions ?? '',
    TextSearch: p.textSearch ?? '',
    Page: p.page ?? 1,
    PageSize: p.pageSize ?? 50,
    Skip: p.skip ?? 0,
    IncludeDeleted: typeof p.includeDeleted === 'boolean' ? p.includeDeleted : false,
    // Some backends validate Required for OrderByField (empty string may fail). Default to Name.
    OrderByField: (p.orderByField ?? 'Name'),
    Descending: typeof p.descending === 'boolean' ? p.descending : false,
  };
  const { data } = await axios.get<RolesApiResponse>('/Roles', { params: query });
  return data;
}

export async function fetchRoleById(id: string): Promise<RoleApiItem> {
  const { data } = await axios.get<RoleApiItem>(`/Roles/${encodeURIComponent(id)}`);
  return data;
}

export interface CreateRolePayload {
  name: string;
  description: string;
  permissions: string[];
  id?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export async function createRole(payload: CreateRolePayload): Promise<RoleApiItem> {
  const body = {
    id: payload.id ?? "",
    name: payload.name,
    description: payload.description,
    permissions: payload.permissions,
    createdAt: payload.createdAt ?? null,
    updatedAt: payload.updatedAt ?? null,
  };
  const { data } = await axios.post<RoleApiItem>('/Roles', body, {
    headers: { 'Content-Type': 'application/json' },
  });
  return data;
}

export interface UpdateRolePayload {
  name: string;
  description: string;
  permissions: string[];
  id?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export async function updateRole(id: string, payload: UpdateRolePayload): Promise<RoleApiItem> {
  const body = {
    id: payload.id ?? id,
    name: payload.name,
    description: payload.description,
    permissions: payload.permissions,
    createdAt: payload.createdAt ?? null,
    updatedAt: payload.updatedAt ?? null,
  };
  const { data } = await axios.put<RoleApiItem>(`/Roles/${encodeURIComponent(id)}`, body, {
    headers: { 'Content-Type': 'application/json' },
  });
  return data;
}

export async function deleteRoleById(id: string): Promise<void> {
  await axios.delete(`/Roles/${encodeURIComponent(id)}`);
}
